import React, { useEffect, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useProfessions } from '../../../../hooks/useProfessions';

export interface AreaOfFocusOption {
  id: number;
  name: string;
  professionName: string;
}

interface Props {
  value: AreaOfFocusOption | null;
  onChange: (value: AreaOfFocusOption | null) => void;
}

// Store-based picker mirroring the affinity SubProfessionPicker, but over Areas
// of Focus (areaOfFocusLibrary keyed by profession id), grouped under each
// parent profession.
export const AreaOfFocusPicker: React.FC<Props> = ({ value, onChange }) => {
  const { professionsList, areaOfFocusLibrary, getProfessions, areProfessionsLoaded } =
    useProfessions();

  useEffect(() => {
    if (!areProfessionsLoaded) getProfessions();
  }, [areProfessionsLoaded, getProfessions]);

  const options = useMemo<AreaOfFocusOption[]>(() => {
    const out: AreaOfFocusOption[] = [];
    (professionsList || []).forEach((prof: { id: number; name: string }) => {
      const areas = areaOfFocusLibrary?.[prof.id] || [];
      areas.forEach((aof: { id: number; name: string }) => {
        out.push({ id: aof.id, name: aof.name, professionName: prof.name });
      });
    });
    return out.sort(
      (a, b) =>
        a.professionName.localeCompare(b.professionName) || a.name.localeCompare(b.name),
    );
  }, [professionsList, areaOfFocusLibrary]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      options={options}
      groupBy={(o) => o.professionName}
      getOptionLabel={(o) => o.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <TextField {...params} label="Area of focus" placeholder="Search…" size="small" />
      )}
      sx={{ width: '100%' }}
    />
  );
};
